/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@progress/kendo-react-tooltip'

import { AsyncGroupedComboBox } from '@components/common/AsyncGroupedComboBox'
import { searchObjects } from '../../../../../../api/commonApi'
import { cn } from '@utils/mergeClassNames'
import CLOSE_ICON from '@public/assets/action/close_circle.svg'
import WARNING_ICON from '@public/assets/action/warning-circle.svg'
import SEARCH_ICON from '@public/assets/action/icon_zoom.svg'
import MATTER_ICON from '@public/assets/common/Icon_Matter.svg'

const getCodeFieldRootClasses = (status, tone = 'matter') =>
  cn('code-field group min-w-[104px] text-sm', tone, status)

const getCodeFieldDisplayClasses = (
  status,
  showEmptyBackground = false,
  hasValue = false
) =>
  cn(
    'relative flex min-h-10 min-w-0 cursor-pointer items-center gap-2 rounded-md py-0 pl-1 text-sm transition-colors [&>div:not(.absolute)]:min-w-0 [&>div:not(.absolute)]:flex-1 [&>div:not(.absolute)]:overflow-hidden',
    hasValue ? 'pr-11' : 'pr-1',
    (status === 'hover' || status === 'filled') && 'bg-[#E9F5FE]',
    status === 'default' && showEmptyBackground && 'bg-[#f5f5f5]',
    status === 'focus' && 'bg-[#90CBF9]'
  )

const getCodeFieldBorderClasses = (
  status,
  hideEmptyBorderUntilHover = false,
  useNeutralBorder = false
) =>
  cn(
    'h-0.5 shrink-0 self-stretch rounded-sm -translate-y-0.5',
    status === 'error'
      ? 'bg-[#B3261E]'
      : status === 'filled'
        ? cn(
            'bg-transparent',
            useNeutralBorder
              ? 'group-hover:bg-[#D9D9D9]'
              : 'group-hover:bg-[#90CBF9]'
          )
        : useNeutralBorder
          ? status === 'default' && hideEmptyBorderUntilHover
            ? 'bg-transparent'
            : 'bg-[#D9D9D9]'
          : 'bg-[#90CBF9]'
  )

const EditableMatterField = ({
  className = '',
  value,
  displayValue,
  secondaryDisplayValue,
  tooltipValue,
  onChange,
  onBeforeEdit,
  setClientVersion,
  matterVersion,
  setMatterVersion,
  tempMatters,
  setTempMatters,
  formData,
  setFormData,
  error,
  clearError,
  hasDestination,
  required = true,
  matchPopupWidth = false,
  showLeadingIcon = true,
  showEmptyLabel = true,
  showEmptyLabelOnHoverOnly = false,
  showEmptyBackground = false,
  useNeutralBorder = false,
}) => {
  const [status, setStatus] = useState('default')
  const anchorRef = useRef(null)
  const comboRef = useRef(null)

  const handleEditStart = async () => {
    if (!onBeforeEdit) {
      setStatus('focus')
      return
    }

    const canEdit = await onBeforeEdit()
    if (canEdit === false) return

    requestAnimationFrame(() => {
      requestAnimationFrame(() => setStatus('focus'))
    })
  }

  useEffect(() => {
    if (error) setStatus('error')
    else if (value) setStatus('filled')
  }, [value, error])

  useEffect(() => {
    const handleClickOutside = event => {
      setTimeout(() => {
        const dropdown = document.querySelector('.k-animation-container')
        const comboEl = comboRef.current?.element
        const ignoreClasses = ['k-i-x', 'k-input-inner', 'k-i-caret-alt-down']
        const clickInsideIgnored = ignoreClasses.some(className =>
          event.target.closest(`.${className}`)
        )

        const clickInsideCombo = comboEl?.contains(event.target)
        const clickInsideDropdown = dropdown?.contains(event.target)

        if (
          status === 'focus' &&
          !clickInsideCombo &&
          !clickInsideDropdown &&
          !clickInsideIgnored
        ) {
          setStatus(value ? 'filled' : 'default')
        }
      }, 0)
    }

    document.addEventListener('mouseup', handleClickOutside)
    return () => document.removeEventListener('mouseup', handleClickOutside)
  }, [status, value])

  return (
    <div
      className={cn(getCodeFieldRootClasses(status, 'matter'), className)}
      onMouseEnter={() => status === 'default' && setStatus('hover')}
      onMouseLeave={() => status === 'hover' && setStatus('default')}
    >
      {status === 'focus' ? (
        <AsyncGroupedComboBox
          ref={comboRef}
          id="new-matter"
          valid={error}
          key={`matter-${matterVersion}`}
          defaultOpen
          localAddedOptions={tempMatters}
          hasDestination={hasDestination}
          clientNumber={formData?.client_number ?? null}
          onAddNew={newMatter => setTempMatters(prev => [...prev, newMatter])}
          value={value}
          onChange={(value, raw) => {
            const normalize = nextValue =>
              Array.isArray(nextValue) ? nextValue[0] : nextValue
            const client_number = normalize(raw?.client_number) ?? null
            const client_name = normalize(raw?.client_name) ?? null
            const client_id = normalize(raw?.client_id) ?? null
            const matter_name = normalize(raw?.name) ?? null
            const matter_id = normalize(raw?.obj_id) ?? null
            const matter_type = normalize(raw?.type) ?? null
            const matterBillable = raw?.is_billable

            setFormData(prev => ({
              ...prev,
              matter_number: value,
              matter_name,
              matter_id,
              matter_type,
              client_number,
              client_name,
              client_id,
              is_billable: matterBillable ?? prev.is_billable,
            }))

            if (formData.client_number !== client_number && hasDestination) {
              setClientVersion(prev => prev + 1)
              setMatterVersion(prev => prev + 1)
            }

            if (!value) return
            clearError('client_number')
            setTimeout(() => setStatus(value ? 'filled' : 'default'), 100)
          }}
          fetchOptions={async query => {
            const filter = formData.client_number
              ? `client_number:(${formData.client_number})`
              : ''
            return searchObjects(
              'matter',
              query,
              0,
              40,
              'obj_id,name,number,client_id,client_number,client_name,is_billable,type,utbms_required',
              filter,
              hasDestination
            )
          }}
          config={{
            field: 'matter',
            labelFields: ['number', 'name'],
            value: 'number',
            separator: ' ',
            valueLabel: 'Matter Number',
            nameLabel: 'Matter Name',
            typeLabel: 'Matter',
          }}
          placeholder="Search matter"
          matchPopupWidth={matchPopupWidth}
        />
      ) : status === 'error' ? (
        <>
          <div
            className="box-border flex h-10 cursor-pointer items-center gap-2 rounded border border-[#B3261E] px-1"
            onClick={() => {
              clearError?.('matter_number')
              setStatus('focus')
            }}
          >
            <img className="h-4 w-4" src={MATTER_ICON} alt="" />
            {/* flex-1 pushes the warning and search icons to the right edge. */}
            <span className="min-w-[60px] flex-1 text-sm font-normal text-[#999]">
              {required && (
                <span className="text-sm font-normal text-[#F55A4E]">*</span>
              )}
              Matter
            </span>
            <img className="h-4 w-4" src={WARNING_ICON} alt="warning" />
            <img className="search" src={SEARCH_ICON} alt="" />
          </div>
          {error && <div className="error-text">{error}</div>}
        </>
      ) : (
        <>
          <div
            ref={anchorRef}
            className={getCodeFieldDisplayClasses(
              status,
              showEmptyBackground && !value,
              Boolean(value)
            )}
            onMouseDown={() => void handleEditStart()}
          >
            {showLeadingIcon && (
              <img className="h-4 w-4" src={MATTER_ICON} alt="" />
            )}
            {!value &&
              showEmptyLabel &&
              (!showEmptyLabelOnHoverOnly || status === 'hover') && (
                <span className="w-[60px] shrink-0 pr-2 text-sm font-normal text-[#999]">
                  {required && (
                    <span className="text-sm font-normal text-[#F55A4E]">
                      *
                    </span>
                  )}
                  Matter
                </span>
              )}

            {value && (
              <Tooltip
                tooltipStyle={{
                  maxWidth: '264px',
                  border: 'none',
                  backgroundColor: '#000',
                  color: '#fff',
                  padding: '4px 8px',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
                anchorElement="target"
                position="top"
              >
                <span className="matter-field-value flex w-full min-w-0 max-w-[320px] flex-1 flex-col">
                  <span
                    title={`${tooltipValue}`}
                    className="cursor-default truncate"
                  >
                    {displayValue}
                  </span>
                  {secondaryDisplayValue && (
                    <span className="truncate text-xs leading-4 text-[#666]">
                      {secondaryDisplayValue}
                    </span>
                  )}
                </span>
              </Tooltip>
            )}

            <div className="absolute right-1 flex shrink-0 items-center gap-1.5">
              {value && (
                <img
                  className="cursor-pointer"
                  src={CLOSE_ICON}
                  onMouseDown={event => event.stopPropagation()}
                  onClick={event => {
                    event.stopPropagation()
                    onChange(null)
                  }}
                  alt="clear"
                />
              )}
              <img className="search" src={SEARCH_ICON} alt="" />
            </div>
          </div>

          <div
            className={getCodeFieldBorderClasses(
              status,
              showEmptyLabelOnHoverOnly,
              useNeutralBorder
            )}
          />
        </>
      )}
    </div>
  )
}

export default EditableMatterField
